import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { wyscoutMatchesAPI } from '@/services/api';
import DelayedLoading from '@/components/ui/DelayedLoading';
import LoadingStatus from '@/components/ui/LoadingStatus';
import PlayerEventAnalysis from './PlayerEventAnalysis';
import WyscoutMatchFilters from './WyscoutMatchFilters';
import WyscoutMatchTable from './WyscoutMatchTable';
import {
  activeWyscoutFilterCount,
  aggregateWyscoutFixtures,
  defaultWyscoutMatchFilters,
  filterWyscoutFixtures,
  updateWyscoutMatchFilters,
  WyscoutMatchFilters as FilterValues,
} from './wyscoutMatchFilters';

interface WyscoutStatsSectionProps {
  playerId: string;
  wyscoutPlayerId?: number | null;
  showEventAnalysis?: boolean;
  selectedMatchId?: number | null;
  onOpenFixture?: (matchId: number, trigger: HTMLButtonElement) => void;
}

type StatsData = Awaited<ReturnType<typeof wyscoutMatchesAPI.getPlayerWyscoutStats>>['data'];
type LegacyMatch = StatsData['perMatch'][number];

const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

const formatMatchLabel = (label: string) => label.length > 40 ? `${label.substring(0, 37)}...` : label;

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div aria-label={`${label} summary`} className="rounded-lg bg-gray-50 p-4 text-center dark:bg-gray-800">
    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{label}</div>
  </div>
);

const LegacyHistory: React.FC<{ totals: StatsData['totals']; perMatch: LegacyMatch[] }> = ({ totals, perMatch }) => {
  const passAccuracy = totals.passes > 0 ? `${((totals.successfulPasses / totals.passes) * 100).toFixed(1)}%` : '0%';
  const duelWinRate = totals.duels > 0 ? `${((totals.duelsWon / totals.duels) * 100).toFixed(1)}%` : '0%';

  return (
    <>
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Matches" value={String(totals.matchesPlayed)} />
        <StatCard label="Goals" value={String(totals.goals)} />
        <StatCard label="Assists" value={String(totals.assists)} />
        <StatCard label="xG" value={totals.xg.toFixed(2)} />
        <StatCard label="Minutes" value={String(totals.minutes)} />
        <StatCard label="Pass Acc %" value={passAccuracy} />
        <StatCard label="Duel Win %" value={duelWinRate} />
        <StatCard label="Interceptions" value={String(totals.interceptions)} />
      </div>

      {perMatch.length > 0 && (
        <>
          <h4 className="mb-3 text-base font-medium text-gray-900 dark:text-gray-100">Per-Match Breakdown</h4>
          <div className="overflow-x-auto">
            <table aria-label="Per-match breakdown" className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {['Date', 'Match', 'Pos', 'Min', 'G', 'A', 'xG', 'Sh', 'Pass%', 'KP', 'Duel', 'Won', 'Int', 'Cards'].map((heading, index) => (
                    <th key={heading} scope="col" className={`px-3 py-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400 ${index < 2 ? 'text-left' : 'text-center'}`}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {perMatch.map((match, index) => {
                  const passAccuracyValue = match.passes > 0 ? ((match.successfulPasses / match.passes) * 100).toFixed(0) : '-';
                  return (
                    <tr key={`${match.matchId}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{formatDate(match.dateUtc)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm" title={match.label}>
                        <Link to={`/matches/wyscout/${match.matchId}`} className="text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-400">{formatMatchLabel(match.label)}</Link>
                      </td>
                      <td className="px-2 py-2 text-center text-xs text-gray-500 dark:text-gray-400">{match.position?.substring(0, 3) || '-'}</td>
                      <td className="px-2 py-2 text-center text-sm text-gray-900 dark:text-gray-100">{match.minutes || '-'}</td>
                      <td className="px-2 py-2 text-center text-sm">{match.goals || '-'}</td>
                      <td className="px-2 py-2 text-center text-sm">{match.assists || '-'}</td>
                      <td className="px-2 py-2 text-center text-sm">{match.xg ? match.xg.toFixed(2) : '-'}</td>
                      <td className="px-2 py-2 text-center text-sm">{match.shots || '-'}</td>
                      <td className="px-2 py-2 text-center text-sm">{passAccuracyValue}</td>
                      <td className="px-2 py-2 text-center text-sm">{match.keyPasses || '-'}</td>
                      <td className="px-2 py-2 text-center text-sm">{match.duels || '-'}</td>
                      <td className="px-2 py-2 text-center text-sm">{match.duelsWon || '-'}</td>
                      <td className="px-2 py-2 text-center text-sm">{match.interceptions || '-'}</td>
                      <td className="px-2 py-2 text-center text-sm">{match.yellowCards ? `${match.yellowCards}Y` : ''}{match.redCards ? ` ${match.redCards}R` : ''}{!match.yellowCards && !match.redCards ? '-' : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

const WyscoutStatsSection: React.FC<WyscoutStatsSectionProps> = ({ playerId, wyscoutPlayerId, showEventAnalysis = true, selectedMatchId, onOpenFixture }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [filters, setFilters] = useState<FilterValues>(defaultWyscoutMatchFilters);
  const [filtersHidden, setFiltersHidden] = useState(false);

  useEffect(() => {
    if (wyscoutPlayerId === null) return;

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await wyscoutMatchesAPI.getPlayerWyscoutStats(playerId);
        setStats(response.data);
        setFilters(defaultWyscoutMatchFilters);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Failed to load Wyscout stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [playerId, wyscoutPlayerId]);

  if (wyscoutPlayerId === null) return null;

  if (loading) {
    return (
      <div id="wyscout-section" className="rounded-lg bg-white p-6 shadow dark:bg-gray-900">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Wyscout Stats</h3>
        <DelayedLoading loading><LoadingStatus label="Loading Wyscout player statistics" className="h-24 justify-center" /></DelayedLoading>
      </div>
    );
  }

  if (error) {
    return (
      <div id="wyscout-section" className="rounded-lg bg-white p-6 shadow dark:bg-gray-900">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Wyscout Stats</h3>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20"><p className="text-red-800 dark:text-red-300">{error}</p></div>
      </div>
    );
  }

  if (!stats || (stats.fixtures.length === 0 && (!stats.totals || stats.totals.matchesPlayed === 0))) {
    return (
      <div id="wyscout-section" className="rounded-lg bg-white p-6 shadow dark:bg-gray-900">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Wyscout Stats</h3>
        <p className="text-gray-500 dark:text-gray-400">No Wyscout stats available for this player.</p>
      </div>
    );
  }

  const hasFixtureHistory = stats.fixtures.length > 0;
  const filtered = hasFixtureHistory ? filterWyscoutFixtures(stats.fixtures, filters) : [];
  const summary = aggregateWyscoutFixtures(filtered);
  const activeCount = activeWyscoutFilterCount(filters);
  const patchFilters = (patch: Partial<FilterValues>) => setFilters(current => updateWyscoutMatchFilters(current, patch));
  const clearFilters = () => setFilters(defaultWyscoutMatchFilters);
  const passAccuracy = summary.passes !== null && summary.successfulPasses !== null
    ? summary.passes > 0 ? `${((summary.successfulPasses / summary.passes) * 100).toFixed(1)}%` : '0%'
    : 'N/A';
  const duelWinRate = summary.duels !== null && summary.duelsWon !== null
    ? summary.duels > 0 ? `${((summary.duelsWon / summary.duels) * 100).toFixed(1)}%` : '0%'
    : 'N/A';

  return (
    <div id="wyscout-section" className="rounded-lg bg-white p-4 shadow dark:bg-gray-900 sm:p-6">
      <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Wyscout Stats</h3>

      {hasFixtureHistory ? (
        <>
          <h4 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">Match-by-Match</h4>

          <WyscoutMatchFilters
            values={filters}
            options={stats.filterOptions}
            activeCount={activeCount}
            resultCount={filtered.length}
            totalCount={stats.fixtures.length}
            hidden={filtersHidden}
            onChange={patchFilters}
            onClear={clearFilters}
            onHiddenChange={setFiltersHidden}
          />

          <div className="my-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Fixtures" value={String(summary.fixturesShown)} />
            <StatCard label="Appearances" value={String(summary.appearances)} />
            <StatCard label="Goals" value={summary.goals === null ? 'N/A' : String(summary.goals)} />
            <StatCard label="Assists" value={summary.assists === null ? 'N/A' : String(summary.assists)} />
            <StatCard label="xG" value={summary.xg === null ? 'N/A' : summary.xg.toFixed(2)} />
            <StatCard label="Minutes" value={summary.minutes === null ? 'N/A' : String(summary.minutes)} />
            <StatCard label="Pass Acc %" value={passAccuracy} />
            <StatCard label="Duel Win %" value={duelWinRate} />
            <StatCard label="Interceptions" value={summary.interceptions === null ? 'N/A' : String(summary.interceptions)} />
          </div>

          <div className="mb-4 space-y-1 text-sm text-gray-600 dark:text-gray-300">
            {summary.appearances > 0 && <p>Totals cover {summary.availableAppearances} of {summary.appearances} appearances.</p>}
            {!stats.coverage.nonAppearanceComplete && <p>Appearance-only history. Reimport to discover missed fixtures.</p>}
          </div>

          {filtered.length > 0 ? (
            <WyscoutMatchTable fixtures={filtered} selectedMatchId={selectedMatchId} onOpenFixture={onOpenFixture} />
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 px-4 py-10 text-center dark:border-gray-700">
              <p className="text-gray-600 dark:text-gray-300">No fixtures match these filters.</p>
              <button type="button" className="mt-3 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900" onClick={clearFilters}>Clear filters</button>
            </div>
          )}
        </>
      ) : (
        <LegacyHistory totals={stats.totals} perMatch={stats.perMatch || []} />
      )}

      {showEventAnalysis && wyscoutPlayerId !== undefined && <PlayerEventAnalysis playerId={playerId} wyscoutPlayerId={wyscoutPlayerId} />}
    </div>
  );
};

export default WyscoutStatsSection;
